import { useState } from 'react'
import {
  App as AntdApp,
  Button,
  Card,
  Divider,
  Form,
  Grid,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tabs,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  UserOutlined,
  HomeOutlined,
  BankOutlined,
  SafetyOutlined,
  LockOutlined,
  PlusOutlined,
  NotificationOutlined,
  KeyOutlined,
  CopyOutlined,
  HistoryOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import apiClient from '../../api/client'
import DataTable from '../../components/DataTable'
import PageHeader from '../../components/PageHeader'
import SepaMandateModal from '../../components/SepaMandateModal'

interface Customer {
  id: number
  customer_no: string
  kind: 'person' | 'company'
  first_name?: string
  last_name?: string
  company?: string
  email?: string
  phone?: string
  tax_id?: string
  active: boolean
}

interface Address {
  id: number
  role: 'billing' | 'shipping' | 'seat' | 'other'
  line1: string
  line2?: string
  postal_code?: string
  city: string
  state?: string
  country: string
  is_default: boolean
}

interface Mandate {
  id: number
  mandate_ref: string
  debtor_name: string
  iban_masked: string
  sequence_type: string
  signed_at?: string
  revoked_at?: string
}

interface Consent {
  customer_id: number
  kind: string
  granted: boolean
  created_at: string
}

export default function PortalProfile() {
  const { t } = useTranslation()

  const meQ = useQuery({
    queryKey: ['portal-me'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { customer: Customer; email: string } }>('/api/v1/portal/me')
      return res.data.data
    },
  })

  const mandatesQ = useQuery<Mandate[]>({
    queryKey: ['portal-mandates'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: Mandate[] }>('/api/v1/portal/mandates')
      return res.data.data ?? []
    },
  })

  const displayName = meQ.data?.customer?.company
    || `${meQ.data?.customer?.first_name ?? ''} ${meQ.data?.customer?.last_name ?? ''}`.trim()
    || meQ.data?.email
    || '—'
  const activeMandate = (mandatesQ.data ?? []).find(m => !m.revoked_at)

  return (
    <>
      <PageHeader
        icon={<UserOutlined />}
        title={t('portal.profile.title', 'Mein Profil')}
        subtitle={t('portal.profile.subtitle', 'Stammdaten, Adressen, Zahlungsmandate und Datenschutz-Einstellungen.')}
      />

      <div className="kpi-grid kpi-grid-3 mb-16">
        <div className="kpi-tile kpi-primary">
          <div className="kpi-tile-label">{t('portal.profile.name', 'Name')}</div>
          <div className="kpi-tile-value kpi-tile-value--sm">{displayName}</div>
          <div className="kpi-tile-sub">{meQ.data?.customer?.customer_no ?? ''}</div>
        </div>
        <div className="kpi-tile">
          <div className="kpi-tile-label">{t('portal.profile.kind', 'Kontotyp')}</div>
          <div className="kpi-tile-value kpi-tile-value--sm">
            {meQ.data?.customer?.kind === 'company'
              ? t('portal.profile.company', 'Firma')
              : t('portal.profile.person', 'Privatperson')}
          </div>
        </div>
        <div className={`kpi-tile ${activeMandate ? 'kpi-success' : 'kpi-info'}`}>
          <div className="kpi-tile-label">{t('portal.profile.sepa', 'SEPA-Mandat')}</div>
          <div className="kpi-tile-value kpi-tile-value--sm">
            {activeMandate ? t('portal.profile.mandateActive', 'Aktiv') : t('portal.profile.noMandate', 'Keins')}
          </div>
          {activeMandate && <div className="kpi-tile-sub">{activeMandate.iban_masked}</div>}
        </div>
      </div>

      <Card className="content-card">
        <Tabs
          defaultActiveKey="master"
          items={[
            {
              key: 'master',
              label: <span><UserOutlined /> {t('portal.profile.master', 'Stammdaten')}</span>,
              children: <MasterTab />,
            },
            {
              key: 'addresses',
              label: <span><HomeOutlined /> {t('portal.profile.addresses', 'Adressen')}</span>,
              children: <AddressesTab />,
            },
            {
              key: 'mandates',
              label: <span><BankOutlined /> {t('portal.profile.mandates', 'SEPA-Mandate')}</span>,
              children: <MandatesTab />,
            },
            {
              key: 'consents',
              label: <span><SafetyOutlined /> {t('portal.profile.consents', 'Einwilligungen')}</span>,
              children: <ConsentsTab />,
            },
            {
              key: 'marketing',
              label: <span><NotificationOutlined /> {t('portal.profile.marketing', 'Marketing')}</span>,
              children: <MarketingTab />,
            },
            {
              key: 'password',
              label: <span><LockOutlined /> {t('portal.profile.password', 'Passwort')}</span>,
              children: <PasswordTab />,
            },
            {
              key: '2fa',
              label: <span><KeyOutlined /> {t('portal.profile.twofa', '2-Faktor-Auth')}</span>,
              children: <TwoFATab />,
            },
            {
              key: 'security',
              label: <span><HistoryOutlined /> {t('portal.profile.security', 'Account-Aktivität')}</span>,
              children: <SecurityTab />,
            },
          ]}
        />
      </Card>
    </>
  )
}

// ── Tab 1: Master data ────────────────────────────────────────────────

function MasterTab() {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [emailChangeOpen, setEmailChangeOpen] = useState(false)
  const [emailForm] = Form.useForm()

  const meQ = useQuery({
    queryKey: ['portal-me'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { customer: Customer; email: string } }>('/api/v1/portal/me')
      return res.data.data
    },
  })

  const featuresQ = useQuery({
    queryKey: ['portal-features'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { tax_id_self_edit?: boolean } }>('/api/v1/portal/features')
      return res.data.data
    },
  })
  const taxSelfEdit = !!featuresQ.data?.tax_id_self_edit

  const save = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      await apiClient.patch('/api/v1/portal/me', values)
    },
    onSuccess: () => {
      message.success(t('portal.profile.saved', 'Gespeichert'))
      qc.invalidateQueries({ queryKey: ['portal-me'] })
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('common.saveFailed', 'Speichern fehlgeschlagen')),
  })

  const requestEmailChange = useMutation({
    mutationFn: async (v: { current_password: string; new_email: string }) => {
      await apiClient.post('/api/v1/portal/account/change-email', v)
    },
    onSuccess: () => {
      message.success(t('portal.profile.emailChangeSent',
        'Bestätigungs-E-Mail wurde an Ihre neue Adresse gesendet. Bitte klicken Sie auf den Link in der E-Mail.'))
      setEmailChangeOpen(false)
      emailForm.resetFields()
    },
    onError: (e: { response?: { data?: { error?: string } } }) => {
      message.error(e.response?.data?.error || t('portal.profile.emailChangeFailed', 'E-Mail-Änderung fehlgeschlagen'))
    },
  })

  if (!meQ.data) {
    return <Typography.Text type="secondary">{t('common.loading', 'Lädt…')}</Typography.Text>
  }

  const { customer, email } = meQ.data

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        initialValues={customer}
        onFinish={(v) => save.mutate(v)}
      >
        <Form.Item label={t('portal.profile.email', 'E-Mail (Login)')}>
          <Space.Compact className="w-full">
            <Input disabled value={email || ''} className="flex-1" />
            <Button onClick={() => setEmailChangeOpen(true)}>
              {t('portal.profile.changeEmail', 'Ändern')}
            </Button>
          </Space.Compact>
        </Form.Item>
        <Form.Item label={t('portal.profile.firstName', 'Vorname')} name="first_name">
          <Input />
        </Form.Item>
        <Form.Item label={t('portal.profile.lastName', 'Nachname')} name="last_name">
          <Input />
        </Form.Item>
        <Form.Item label={t('portal.profile.company', 'Firma')} name="company">
          <Input />
        </Form.Item>
        <Form.Item label={t('portal.profile.phone', 'Telefon')} name="phone">
          <Input />
        </Form.Item>
        {taxSelfEdit ? (
          <Form.Item
            label={t('portal.profile.taxId', 'USt-IdNr.')}
            name="tax_id"
            extra={t('portal.profile.taxIdHint', 'Wird automatisch über VIES geprüft.')}
          >
            <Input placeholder="DE123456789" />
          </Form.Item>
        ) : customer.tax_id ? (
          <Form.Item label={t('portal.profile.taxId', 'USt-IdNr.')}>
            <Input disabled value={customer.tax_id} />
          </Form.Item>
        ) : null}
        <Button type="primary" htmlType="submit" loading={save.isPending}>
          {t('common.save', 'Speichern')}
        </Button>
      </Form>

      <Modal
        open={emailChangeOpen}
        onCancel={() => { setEmailChangeOpen(false); emailForm.resetFields() }}
        title={t('portal.profile.changeEmailTitle', 'E-Mail-Adresse ändern')}
        onOk={() => emailForm.submit()}
        okButtonProps={{ loading: requestEmailChange.isPending }}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          {t('portal.profile.changeEmailHint',
            'Geben Sie Ihre neue E-Mail-Adresse und Ihr aktuelles Passwort ein. Wir senden einen Bestätigungslink an die neue Adresse.')}
        </Typography.Paragraph>
        <Form
          form={emailForm}
          layout="vertical"
          onFinish={(v) => requestEmailChange.mutate(v as { current_password: string; new_email: string })}
        >
          <Form.Item
            name="new_email"
            label={t('portal.profile.newEmail', 'Neue E-Mail-Adresse')}
            rules={[{ required: true, type: 'email', message: t('portal.profile.emailInvalid', 'Gültige E-Mail-Adresse erforderlich') }]}
          >
            <Input autoComplete="email" />
          </Form.Item>
          <Form.Item
            name="current_password"
            label={t('portal.pwd.old', 'Aktuelles Passwort')}
            rules={[{ required: true }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

// ── Tab 2: Addresses ──────────────────────────────────────────────────

function AddressesTab() {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const screens = Grid.useBreakpoint()
  const [open, setOpen] = useState<Address | null>(null)
  const [creating, setCreating] = useState(false)
  const [form] = Form.useForm()

  const listQ = useQuery({
    queryKey: ['portal-addresses'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: Address[] }>('/api/v1/portal/addresses')
      return res.data.data
    },
  })

  const save = useMutation({
    mutationFn: async (values: Partial<Address>) => {
      if (open?.id) {
        await apiClient.patch(`/api/v1/portal/addresses/${open.id}`, values)
      } else {
        await apiClient.post('/api/v1/portal/addresses', values)
      }
    },
    onSuccess: () => {
      message.success(t('portal.profile.saved', 'Gespeichert'))
      qc.invalidateQueries({ queryKey: ['portal-addresses'] })
      setOpen(null)
      setCreating(false)
      form.resetFields()
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('common.saveFailed', 'Speichern fehlgeschlagen')),
  })

  const remove = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/v1/portal/addresses/${id}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-addresses'] }),
    onError: () => message.error(t('common.error', 'Fehler beim Löschen')),
  })

  const columns: ColumnsType<Address> = [
    { title: t('portal.addr.role', 'Rolle'), dataIndex: 'role', key: 'role', width: 110,
      render: (v: string) => <Tag>{v}</Tag> },
    { title: t('portal.addr.line', 'Straße'), key: 'line',
      render: (_, r) => `${r.line1}${r.line2 ? ', ' + r.line2 : ''}` },
    { title: t('portal.addr.city', 'Ort'), key: 'city',
      render: (_, r) => `${r.postal_code ?? ''} ${r.city}, ${r.country}` },
    { title: t('portal.addr.default', 'Standard'), dataIndex: 'is_default', key: 'def', width: 90,
      render: (v: boolean) => v ? <Tag color="success">✓</Tag> : null },
    { title: '', key: 'actions', width: 170,
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => { setOpen(r); form.setFieldsValue(r) }}>
            {t('common.edit', 'Bearbeiten')}
          </Button>
          <Popconfirm title={t('common.confirmDelete', 'Wirklich löschen?')}
            onConfirm={() => remove.mutate(r.id)}>
            <Button size="small" danger>{t('common.delete', 'Löschen')}</Button>
          </Popconfirm>
        </Space>
      ) },
  ]

  return (
    <>
      <Space className="toolbar-row">
        <Typography.Text type="secondary">
          {t('portal.addr.intro', 'Adressen für Rechnungen und Lieferungen.')}
        </Typography.Text>
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => { setCreating(true); form.resetFields(); form.setFieldsValue({ role: 'billing', country: 'DE' }) }}>
          {t('portal.addr.create', 'Adresse hinzufügen')}
        </Button>
      </Space>
      <DataTable<Address>
        rowKey="id"
        columns={columns}
        dataSource={listQ.data || []}
        loading={listQ.isLoading}
        size="small"
        renderMobileCard={(r: Address) => (
          <div className="mob-card">
            <div className="flex-between mb-6">
              <span><Tag>{r.role}</Tag> {r.line1}{r.line2 ? ', ' + r.line2 : ''}</span>
              {r.is_default && <Tag color="success">{t('portalProfile.defaultTag', 'Standard')}</Tag>}
            </div>
            <div className="flex-between text-sm" style={{ color: '#64748B' }}>
              <span>{r.postal_code ?? ''} {r.city}, {r.country}</span>
              <Space>
                <Button size="small" onClick={() => { setOpen(r); form.setFieldsValue(r) }}>{t('common.edit', 'Bearbeiten')}</Button>
                <Popconfirm title={t('common.confirmDelete', 'Wirklich löschen?')} onConfirm={() => remove.mutate(r.id)}>
                  <Button size="small" danger>{t('common.delete', 'Löschen')}</Button>
                </Popconfirm>
              </Space>
            </div>
          </div>
        )}
      />
      <Modal
        open={!!open || creating}
        onCancel={() => { setOpen(null); setCreating(false) }}
        title={open ? t('portal.addr.edit', 'Adresse bearbeiten') : t('portal.addr.create', 'Adresse hinzufügen')}
        onOk={() => form.submit()}
        okButtonProps={{ loading: save.isPending }}
        width={screens.sm ? 520 : '95vw'}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
          <Form.Item name="role" label={t('portal.addr.role', 'Rolle')} rules={[{ required: true }]}>
            <Select options={['billing','shipping','seat','other'].map(r => ({ value: r, label: r }))} />
          </Form.Item>
          <Form.Item name="line1" label={t('portal.addr.line1', 'Straße + Hausnr.')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="line2" label={t('portal.addr.line2', 'Adresszusatz')}>
            <Input />
          </Form.Item>
          <Form.Item name="postal_code" label={t('portal.addr.postalCode', 'PLZ')}>
            <Input />
          </Form.Item>
          <Form.Item name="city" label={t('portal.addr.city', 'Ort')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="country" label={t('portal.addr.country', 'Land (ISO-2)')}
            rules={[{ required: true, pattern: /^[A-Z]{2}$/, message: t('portalProfile.countryFormat', 'ISO-2 Code, z.B. DE') }]}>
            <Input maxLength={2} />
          </Form.Item>
          <Form.Item name="is_default" label={t('portal.addr.default', 'Als Standard markieren')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

// ── Tab 3: SEPA mandates ──────────────────────────────────────────────

function MandatesTab() {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const [openCreate, setOpenCreate] = useState(false)

  const listQ = useQuery({
    queryKey: ['portal-mandates'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: Mandate[] }>('/api/v1/portal/mandates')
      return res.data.data
    },
  })

  const revoke = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.post(`/api/v1/portal/mandates/${id}/revoke`)
    },
    onSuccess: () => {
      message.success(t('portal.mandate.revoked', 'Mandat widerrufen'))
      qc.invalidateQueries({ queryKey: ['portal-mandates'] })
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('common.error', 'Widerruf fehlgeschlagen')),
  })

  const columns: ColumnsType<Mandate> = [
    { title: t('portal.mandate.ref', 'Mandat-Ref.'), dataIndex: 'mandate_ref', key: 'ref',
      render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
    { title: t('portal.mandate.debtor', 'Kontoinhaber'), dataIndex: 'debtor_name', key: 'debtor' },
    { title: 'IBAN', dataIndex: 'iban_masked', key: 'iban',
      render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
    { title: t('portal.mandate.status', 'Status'), key: 'status', width: 130,
      render: (_, r) => r.revoked_at
        ? <Tag color="red">{t('portal.mandate.revoked', 'Widerrufen')}</Tag>
        : <Tag color="success">{t('portal.mandate.active', 'Aktiv')}</Tag> },
    { title: '', key: 'actions', width: 130,
      render: (_, r) => !r.revoked_at ? (
        <Popconfirm title={t('portal.mandate.revokeConfirm', 'Mandat widerrufen?')} onConfirm={() => revoke.mutate(r.id)}>
          <Button size="small" danger>{t('portal.mandate.revoke', 'Widerrufen')}</Button>
        </Popconfirm>
      ) : null },
  ]

  return (
    <>
      <Space className="toolbar-row">
        <Typography.Text type="secondary">
          {t('portal.mandate.intro', 'Ermächtigen Sie uns zur SEPA-Lastschrift Ihrer Rechnungen.')}
        </Typography.Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpenCreate(true)}>
          {t('portal.mandate.create', 'Mandat erteilen')}
        </Button>
      </Space>
      <DataTable<Mandate>
        rowKey="id"
        columns={columns}
        dataSource={listQ.data || []}
        loading={listQ.isLoading}
        size="small"
        renderMobileCard={(r: Mandate) => (
          <div className="mob-card">
            <div className="flex-between mb-6">
              <Typography.Text code>{r.mandate_ref}</Typography.Text>
              {r.revoked_at
                ? <Tag color="red">{t('portal.mandate.revoked', 'Widerrufen')}</Tag>
                : <Tag color="success">{t('portal.mandate.active', 'Aktiv')}</Tag>}
            </div>
            <div className="flex-between text-sm" style={{ color: '#64748B' }}>
              <span>{r.debtor_name} · <Typography.Text code>{r.iban_masked}</Typography.Text></span>
              {!r.revoked_at && (
                <Popconfirm title={t('portal.mandate.revokeConfirm', 'Mandat widerrufen?')} onConfirm={() => revoke.mutate(r.id)}>
                  <Button size="small" danger>{t('portal.mandate.revoke', 'Widerrufen')}</Button>
                </Popconfirm>
              )}
            </div>
          </div>
        )}
      />
      <SepaMandateModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['portal-mandates'] }); setOpenCreate(false) }}
      />
    </>
  )
}

// ── Tab 4: Consents ────────────────────────────────────────────────────

function ConsentsTab() {
  const { t } = useTranslation()

  const consentKinds = [
    { key: 'newsletter', label: 'Newsletter',
      hint: t('portalProfile.consentHint.newsletter', 'Produktinfos und Angebote per E-Mail') },
    { key: 'tracking', label: 'Tracking',
      hint: t('portalProfile.consentHint.tracking', 'Statistische Analyse Ihrer Portal-Nutzung') },
    { key: 'sms', label: 'SMS',
      hint: t('portalProfile.consentHint.sms', 'Wichtige Service-Hinweise per SMS') },
    { key: 'profiling', label: 'Profiling',
      hint: t('portalProfile.consentHint.profiling', 'Personalisierte Empfehlungen') },
  ]
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()

  const listQ = useQuery({
    queryKey: ['portal-consents'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: Consent[] }>('/api/v1/portal/consents')
      return res.data.data
    },
  })

  const record = useMutation({
    mutationFn: async (v: { kind: string; granted: boolean }) => {
      await apiClient.post('/api/v1/portal/consents', v)
    },
    onSuccess: () => {
      message.success(t('portal.consent.saved', 'Einwilligung gespeichert'))
      qc.invalidateQueries({ queryKey: ['portal-consents'] })
    },
    onError: () => message.error(t('common.saveFailed', 'Speichern fehlgeschlagen')),
  })

  const isGranted = (kind: string) =>
    listQ.data?.find((c) => c.kind === kind)?.granted ?? false

  return (
    <Space direction="vertical" className="w-full">
      <Typography.Paragraph type="secondary">
        {t('portal.consent.intro', 'Sie können jede Einwilligung jederzeit ändern. Widerruf wirkt ab sofort.')}
      </Typography.Paragraph>
      {consentKinds.map((c) => (
        <Card key={c.key} size="small">
          <Space className="w-full flex-between">
            <div>
              <Typography.Text strong>{c.label}</Typography.Text>
              <br />
              <Typography.Text type="secondary" className="text-sm">{c.hint}</Typography.Text>
            </div>
            <Switch
              checked={isGranted(c.key)}
              loading={record.isPending}
              onChange={(g) => record.mutate({ kind: c.key, granted: g })}
            />
          </Space>
        </Card>
      ))}
    </Space>
  )
}

// ── Tab 5: Password ────────────────────────────────────────────────────

function PasswordTab() {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const [form] = Form.useForm()

  const change = useMutation({
    mutationFn: async (v: { old_password: string; new_password: string }) => {
      await apiClient.post('/api/v1/portal/account/change-password', v)
    },
    onSuccess: () => {
      message.success(t('portal.pwd.changed', 'Passwort geändert'))
      form.resetFields()
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } }
      message.error(err.response?.data?.error || t('portal.pwd.failed', 'Änderung fehlgeschlagen'))
    },
  })

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(v) => change.mutate(v as { old_password: string; new_password: string })}
      className="max-w-420"
    >
      <Form.Item name="old_password" label={t('portal.pwd.old', 'Aktuelles Passwort')}
        rules={[{ required: true }]}>
        <Input.Password autoComplete="current-password" />
      </Form.Item>
      <Form.Item name="new_password" label={t('portal.pwd.new', 'Neues Passwort')}
        rules={[{ required: true, min: 12, message: t('portal.pwd.rule', 'Mindestens 12 Zeichen.') }]}>
        <Input.Password autoComplete="new-password" />
      </Form.Item>
      <Form.Item dependencies={['new_password']} label={t('portal.pwd.confirm', 'Bestätigen')}
        name="confirm" rules={[{ required: true }, ({ getFieldValue }) => ({
          validator(_, value) {
            if (!value || value === getFieldValue('new_password')) return Promise.resolve()
            return Promise.reject(new Error(t('portal.pwd.mismatch', 'Passwörter stimmen nicht überein.')))
          },
        })]}>
        <Input.Password autoComplete="new-password" />
      </Form.Item>
      <Button type="primary" htmlType="submit" loading={change.isPending}>
        {t('portal.pwd.submit', 'Passwort ändern')}
      </Button>
    </Form>
  )
}

// ── Tab 6: Marketing-Opt-In ───────────────────────────────────────────

function MarketingTab() {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()

  const prefs = useQuery({
    queryKey: ['portal-marketing-prefs'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: {
        marketing_opt_in: boolean
        marketing_opted_out_at?: string | null
      } }>('/api/v1/portal/marketing-preferences')
      return res.data.data
    },
  })

  const update = useMutation({
    mutationFn: async (optIn: boolean) => {
      await apiClient.put('/api/v1/portal/marketing-preferences', { opt_in: optIn })
    },
    onSuccess: () => {
      message.success(t('portal.marketing.saved', 'Einstellung gespeichert'))
      qc.invalidateQueries({ queryKey: ['portal-marketing-prefs'] })
    },
    onError: () => message.error(t('common.saveFailed', 'Speichern fehlgeschlagen')),
  })

  const nl = useQuery({
    queryKey: ['portal-newsletter'],
    queryFn: async () =>
      (await apiClient.get<{ data: { subscribed: boolean; email: string } }>('/api/v1/portal/newsletter')).data.data,
  })
  const nlUpdate = useMutation({
    mutationFn: async (sub: boolean) =>
      apiClient.post(`/api/v1/portal/newsletter/${sub ? 'subscribe' : 'unsubscribe'}`),
    onSuccess: () => {
      message.success(t('portal.marketing.saved', 'Einstellung gespeichert'))
      qc.invalidateQueries({ queryKey: ['portal-newsletter'] })
    },
    onError: () => message.error(t('common.saveFailed', 'Speichern fehlgeschlagen')),
  })

  return (
    <div>
      <Typography.Paragraph>
        {t('portal.marketing.intro',
          'Möchten Sie gelegentlich Informationen zu neuen Diensten, Wartungs-Hinweisen oder Sonderaktionen per E-Mail erhalten? Sie können diese Einwilligung jederzeit widerrufen — über diesen Schalter oder den Abmelde-Link in jeder Marketing-Mail.')}
      </Typography.Paragraph>

      <Space align="center" size="middle" className="mb-12">
        <Switch
          checked={!!prefs.data?.marketing_opt_in}
          loading={prefs.isLoading || update.isPending}
          onChange={(v) => update.mutate(v)}
        />
        <Typography.Text strong>
          {prefs.data?.marketing_opt_in
            ? t('portal.marketing.on', 'Marketing-Mails sind aktiviert')
            : t('portal.marketing.off', 'Marketing-Mails sind deaktiviert')}
        </Typography.Text>
      </Space>

      {prefs.data?.marketing_opted_out_at && (
        <Typography.Paragraph type="secondary">
          {t('portal.marketing.optedOutAt', 'Zuletzt widerrufen am')}{': '}
          {new Date(prefs.data.marketing_opted_out_at).toLocaleString('de-DE')}
        </Typography.Paragraph>
      )}

      <Divider />
      <Typography.Paragraph>
        {t('portal.newsletter.intro',
          'Abonnieren Sie unseren Newsletter für Neuigkeiten, Tipps und Produkt-Updates. Die Abmeldung ist jederzeit möglich.')}
      </Typography.Paragraph>
      <Space align="center" size="middle" className="mb-12">
        <Switch
          checked={!!nl.data?.subscribed}
          loading={nl.isLoading || nlUpdate.isPending}
          onChange={(v) => nlUpdate.mutate(v)}
        />
        <Typography.Text strong>
          {nl.data?.subscribed
            ? t('portal.newsletter.on', 'Newsletter abonniert')
            : t('portal.newsletter.off', 'Newsletter nicht abonniert')}
        </Typography.Text>
      </Space>
      {nl.data?.email && (
        <Typography.Paragraph type="secondary" className="text-xs">
          {t('portal.newsletter.emailHint', 'Zustellung an')}{': '}{nl.data.email}
        </Typography.Paragraph>
      )}

      <Typography.Paragraph type="secondary" className="text-xs">
        {t('portal.marketing.legalHint',
          'Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung). Der Widerruf wirkt sich nicht auf bereits versendete E-Mails aus.')}
      </Typography.Paragraph>
    </div>
  )
}

// ── Tab 7: 2FA (Phase 49) ─────────────────────────────────────────────

interface TwoFAMe {
  totp_enabled: boolean
  totp_enrolled_at?: string | null
}

interface TwoFASetupResult {
  secret: string
  otpauth_url: string
  recovery_codes: string[]
}

function TwoFATab() {
  const { t } = useTranslation()
  const { message, modal } = AntdApp.useApp()
  const qc = useQueryClient()
  const [enrolled, setEnrolled] = useState<TwoFASetupResult | null>(null)
  const [confirmCode, setConfirmCode] = useState('')

  const meQ = useQuery({
    queryKey: ['portal-me-2fa'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: TwoFAMe }>('/api/v1/portal/auth/me')
      return res.data.data
    },
  })

  const setupMut = useMutation({
    mutationFn: async (password: string) => {
      const res = await apiClient.post<{ data: TwoFASetupResult }>(
        '/api/v1/portal/2fa/setup', { password })
      return res.data.data
    },
    onSuccess: (d) => setEnrolled(d),
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('portal.twofa.setupFailed', 'Setup fehlgeschlagen')),
  })

  const confirmMut = useMutation({
    mutationFn: async () => {
      await apiClient.post('/api/v1/portal/2fa/confirm', { code: confirmCode })
    },
    onSuccess: () => {
      message.success(t('portal.twofa.enabled', '2FA wurde aktiviert'))
      setEnrolled(null)
      setConfirmCode('')
      qc.invalidateQueries({ queryKey: ['portal-me-2fa'] })
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('portal.twofa.confirmFailed', 'Code ungültig')),
  })

  const disableMut = useMutation({
    mutationFn: async (v: { password: string; code: string }) => {
      await apiClient.post('/api/v1/portal/2fa/disable', v)
    },
    onSuccess: () => {
      message.success(t('portal.twofa.disabled', '2FA wurde deaktiviert'))
      qc.invalidateQueries({ queryKey: ['portal-me-2fa'] })
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('portal.twofa.disableFailed', 'Deaktivierung fehlgeschlagen')),
  })

  const askSetup = () => {
    let pwd = ''
    modal.confirm({
      title: t('portal.twofa.setupTitle', '2FA einrichten'),
      content: (
        <>
          <Typography.Paragraph>
            {t('portal.twofa.setupPwdHint', 'Bitte bestätige mit deinem aktuellen Passwort.')}
          </Typography.Paragraph>
          <Input.Password
            placeholder={t('portal.pwd.old', 'Aktuelles Passwort')}
            onChange={(e) => { pwd = e.target.value }}
          />
        </>
      ),
      okText: t('portal.twofa.setupBtn', 'Weiter'),
      onOk: () => setupMut.mutateAsync(pwd),
    })
  }

  const askDisable = () => {
    let pwd = ''
    let code = ''
    modal.confirm({
      title: t('portal.twofa.disableTitle', '2FA deaktivieren?'),
      content: (
        <>
          <Typography.Paragraph type="warning">
            {t('portal.twofa.disableWarning',
              'Damit ist dein Konto nur noch durch das Passwort geschützt. Bitte gib zur Bestätigung dein aktuelles Passwort ein.')}
          </Typography.Paragraph>
          <Input.Password
            placeholder={t('portal.pwd.old', 'Aktuelles Passwort')}
            onChange={(e) => { pwd = e.target.value }}
          />
          <Input
            className="mt-12"
            placeholder={t('portal.twofa.codeOrRecovery', '6-stelliger Code oder Recovery-Code')}
            onChange={(e) => { code = e.target.value }}
          />
        </>
      ),
      okText: t('portal.twofa.confirmDisable', 'Deaktivieren'),
      okButtonProps: { danger: true },
      onOk: () => disableMut.mutateAsync({ password: pwd, code }),
    })
  }

  if (meQ.isLoading) return null

  const enabled = meQ.data?.totp_enabled === true

  return (
    <div>
      <Typography.Paragraph>
        {t('portal.twofa.intro',
          'Zusätzlicher Schutz für dein Konto: Nach Eingabe deines Passworts brauchst du noch einen 6-stelligen Code aus einer Authenticator-App (z.B. Google Authenticator, Authy, 1Password).')}
      </Typography.Paragraph>

      {enabled ? (
        <>
          <Tag color="success" className="mb-12">
            {t('portal.twofa.statusEnabled', '2FA ist aktiv')}
          </Tag>
          {meQ.data?.totp_enrolled_at && (
            <Typography.Paragraph type="secondary">
              {t('portal.twofa.enrolledAt', 'Aktiviert am')}:{' '}
              {new Date(meQ.data.totp_enrolled_at).toLocaleString('de-DE')}
            </Typography.Paragraph>
          )}
          <Button danger onClick={askDisable} loading={disableMut.isPending}>
            {t('portal.twofa.disable', '2FA deaktivieren')}
          </Button>
        </>
      ) : enrolled ? (
        <>
          <Typography.Title level={5}>
            {t('portal.twofa.scanTitle', 'Schritt 1: Mit App scannen')}
          </Typography.Title>
          <Typography.Paragraph>
            {t('portal.twofa.scanHint',
              'Öffne deine Authenticator-App und scanne den QR-Code, oder gib das Secret manuell ein:')}
          </Typography.Paragraph>
          <Typography.Text code copyable={{ icon: <CopyOutlined /> }}>
            {enrolled.secret}
          </Typography.Text>
          <Typography.Paragraph className="mt-12 text-xs" type="secondary">
            {t('portal.twofa.urlForQR', 'otpauth-URL (für QR-Generator):')}
            <br />
            <Typography.Text code copyable className="text-xs">{enrolled.otpauth_url}</Typography.Text>
          </Typography.Paragraph>

          <Typography.Title level={5} className="mt-16">
            {t('portal.twofa.recoveryTitle', 'Schritt 2: Recovery-Codes speichern')}
          </Typography.Title>
          <Typography.Paragraph type="warning">
            {t('portal.twofa.recoveryWarning',
              'Diese 8 Codes funktionieren wenn du deine Authenticator-App verlierst. JEDER Code ist EINMAL gültig. Bitte JETZT speichern — sie werden nicht erneut angezeigt!')}
          </Typography.Paragraph>
          <pre className="content-pre">
            {enrolled.recovery_codes.join('\n')}
          </pre>
          <Button
            icon={<CopyOutlined />}
            onClick={() => {
              navigator.clipboard.writeText(enrolled.recovery_codes.join('\n'))
              message.success(t('portal.twofa.codesCopied', 'Recovery-Codes kopiert'))
            }}>
            {t('portal.twofa.copyCodes', 'Codes kopieren')}
          </Button>

          <Typography.Title level={5} className="mt-16">
            {t('portal.twofa.confirmTitle', 'Schritt 3: Erstmals bestätigen')}
          </Typography.Title>
          <Input
            value={confirmCode}
            onChange={(e) => setConfirmCode(e.target.value)}
            placeholder="123456"
            maxLength={6}
            inputMode="numeric"
            className="max-w-200"
          />
          <div className="mt-12">
            <Space>
              <Button
                type="primary"
                onClick={() => confirmMut.mutate()}
                loading={confirmMut.isPending}
                disabled={confirmCode.length !== 6}
              >
                {t('portal.twofa.activate', '2FA aktivieren')}
              </Button>
              <Button onClick={() => setEnrolled(null)}>
                {t('common.cancel', 'Abbrechen')}
              </Button>
            </Space>
          </div>
        </>
      ) : (
        <Button
          type="primary"
          icon={<KeyOutlined />}
          onClick={askSetup}
          loading={setupMut.isPending}
        >
          {t('portal.twofa.setup', '2FA jetzt einrichten')}
        </Button>
      )}
    </div>
  )
}

// ── Tab 8: Security / Account-Activity ─────────────────────────────────

interface SecurityEvent {
  id: number
  action: string
  ip?: string | null
  user_agent?: string
  created_at: string
}

function SecurityTab() {
  const { t } = useTranslation()

  const getSecurityActionLabel = (action: string) => {
    const map: Record<string, string> = {
      portal_login: t('portalProfile.secAction.portalLogin', 'Anmeldung erfolgreich'),
      portal_login_failed: t('portalProfile.secAction.portalLoginFailed', 'Fehlgeschlagene Anmeldung'),
      password_reset_request: t('portalProfile.secAction.passwordResetRequest', 'Passwort-Reset angefordert'),
      password_reset_used: t('portalProfile.secAction.passwordResetUsed', 'Passwort zurückgesetzt'),
      email_verified: t('portalProfile.secAction.emailVerified', 'E-Mail bestätigt'),
      email_verification_resent: t('portalProfile.secAction.emailVerificationResent', 'Bestätigungs-Mail erneut gesendet'),
      totp_enrolled: t('portalProfile.secAction.totpEnrolled', '2-Faktor-Auth aktiviert'),
      totp_disabled: t('portalProfile.secAction.totpDisabled', '2-Faktor-Auth deaktiviert'),
      affiliate_signup: t('portalProfile.secAction.affiliateSignup', 'Affiliate-Anmeldung'),
      referral_attach: t('portalProfile.secAction.referralAttach', 'Referral-Code eingelöst'),
    }
    return map[action] ?? action
  }
  const activityQ = useQuery({
    queryKey: ['portal-security-activity'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: SecurityEvent[] } }>(
        '/api/v1/portal/security/activity?limit=50')
      return res.data.data.items
    },
  })

  const columns: ColumnsType<SecurityEvent> = [
    { title: t('portal.security.when', 'Zeitpunkt'), dataIndex: 'created_at',
      key: 'when', width: 180,
      render: (v: string) => new Date(v).toLocaleString('de-DE') },
    { title: t('portal.security.event', 'Ereignis'), dataIndex: 'action',
      key: 'event', width: 240,
      render: (v: string) => {
        const isFailed = v === 'portal_login_failed'
        const isSensitive = v === 'totp_disabled' || v === 'password_reset_used'
        return (
          <Tag color={isFailed ? 'red' : isSensitive ? 'orange' : 'blue'}>
            {getSecurityActionLabel(v)}
          </Tag>
        )
      } },
    { title: t('portal.security.ip', 'IP'), dataIndex: 'ip', key: 'ip', width: 150,
      render: (v: string | null) => v
        ? <Typography.Text code className="text-sm">{v}</Typography.Text>
        : '—' },
    { title: t('portal.security.ua', 'Gerät'), dataIndex: 'user_agent',
      key: 'ua',
      render: (v?: string) => (
        <Typography.Text type="secondary" ellipsis className="text-sm">
          {v || '—'}
        </Typography.Text>
      ) },
  ]

  return (
    <div className="form-section">
      <Typography.Title level={5} className="mt-0">
        <HistoryOutlined /> {t('portal.security.title', 'Account-Aktivität')}
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        {t('portal.security.intro',
          'Übersicht über Anmeldungen, fehlgeschlagene Login-Versuche und sicherheitsrelevante Änderungen an Ihrem Account. Sehen Sie hier einen Ihnen unbekannten Eintrag? Ändern Sie umgehend Ihr Passwort und aktivieren Sie 2-Faktor-Authentifizierung.')}
      </Typography.Paragraph>
      <DataTable<SecurityEvent>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={activityQ.data || []}
        loading={activityQ.isLoading}
        pageSize={20}
        renderMobileCard={(r: SecurityEvent) => {
          const isFailed = r.action === 'portal_login_failed'
          const isSensitive = r.action === 'totp_disabled' || r.action === 'password_reset_used'
          return (
            <div className="mob-card">
              <div className="flex-between mb-6">
                <Tag color={isFailed ? 'red' : isSensitive ? 'orange' : 'blue'}>
                  {getSecurityActionLabel(r.action)}
                </Tag>
                <Typography.Text type="secondary" className="text-sm">
                  {new Date(r.created_at).toLocaleString('de-DE')}
                </Typography.Text>
              </div>
              {r.ip && <Typography.Text code className="text-sm">{r.ip}</Typography.Text>}
            </div>
          )
        }}
      />
    </div>
  )
}
